import React, { useEffect, useState } from "react";
import { api, useLang } from "../lib/providers";
import { PageHeader, Btn, Field, TextInput, TextArea, EmptyState } from "../components/primitives";
import { useConfirm } from "../components/ConfirmDialog";
import PresenceBar from "../components/PresenceBar";
import usePresence from "../hooks/usePresence";
import { Plus, PencilSimple, Trash } from "@phosphor-icons/react";

export default function CategoriesPage() {
  const { t, lang } = useLang();
  const confirm = useConfirm();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = no editor, {} new, obj edit
  const { others } = usePresence({ resource: "categories", enabled: true });
  const editingId = editing?.id || null;
  const { holder: editHolder } = usePresence({ resource: "categories", itemId: editingId, edit: !!editingId, enabled: !!editingId });

  const load = () => {
    setLoading(true);
    api.get("/categories").then((r) => setItems(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const save = async (e) => {
    e.preventDefault();
    const payload = { name: editing.name, description: editing.description || "" };
    if (editing.id) {
      await api.put(`/categories/${editing.id}`, payload);
    } else {
      await api.post("/categories", payload);
    }
    setEditing(null);
    load();
  };

  const del = async (c) => {
    const ok = await confirm({
      title: `${t("confirmDeleteTitle")} ${c.name}`,
      description: t("confirmHardDeleteDesc"),
      confirmText: t("deleteButton"),
      variant: "danger",
    });
    if (!ok) return;
    await api.delete(`/categories/${c.id}`);
    load();
  };

  return (
    <div className="p-6 sm:p-8 max-w-[1200px] mx-auto">
      <PageHeader
        eyebrow="03 / Struktur"
        title={t("categories")}
        description={lang === "de" ? "Oberkategorien strukturieren deinen Positionskatalog." : "Top-level categories structure your catalog."}
        actions={
          <div className="flex items-center gap-3">
            <PresenceBar others={others} lang={lang} />
            <Btn data-testid="new-category-btn" onClick={() => setEditing({ name: "", description: "" })}>
              <Plus size={16} /> {t("newCategory")}
            </Btn>
          </div>
        }
      />

      {editing && (
        <form onSubmit={save} className="swiss-card p-6 mb-6" data-testid="category-form">
          {editHolder && (
            <div className="-m-6 mb-4 px-5 py-3 bg-[#F59E0B]/10 border-b border-[#F59E0B]/30 text-sm text-[#92400E] flex items-center gap-2" data-testid="category-lock-warning">
              <span className="inline-block w-2 h-2 rounded-full bg-[#F59E0B]" />
              {lang === "de"
                ? <>Achtung: <b>{editHolder.user_name || editHolder.user_email}</b> bearbeitet diese Kategorie gerade.</>
                : <>Warning: <b>{editHolder.user_name || editHolder.user_email}</b> is currently editing this category.</>}
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label={t("categoryName")}>
              <TextInput required data-testid="category-name" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
            </Field>
            <Field label={t("description")}>
              <TextInput data-testid="category-description" value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
            </Field>
          </div>
          <div className="flex gap-2 mt-4">
            <Btn type="submit" data-testid="category-save">{t("save")}</Btn>
            <Btn type="button" variant="ghost" onClick={() => setEditing(null)} data-testid="category-cancel">{t("cancel")}</Btn>
          </div>
        </form>
      )}

      {loading ? (
        <div className="swiss-card p-8 text-[var(--muted)]">…</div>
      ) : items.length === 0 ? (
        <EmptyState
          title={t("noCategories")}
          action={<Btn onClick={() => setEditing({ name: "", description: "" })} data-testid="empty-new-category"><Plus size={16} /> {t("newCategory")}</Btn>}
        />
      ) : (
        <div className="swiss-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[var(--bg)] text-[var(--muted)]">
              <tr>
                <th className="text-left font-medium px-5 py-3 label-overline w-24">Nr.</th>
                <th className="text-left font-medium px-5 py-3 label-overline">{t("categoryName")}</th>
                <th className="text-left font-medium px-5 py-3 label-overline">{t("description")}</th>
                <th className="text-right font-medium px-5 py-3 label-overline">{t("actions")}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.id} className="border-t border-[var(--border)] hover:bg-[var(--row-hover)] transition-colors fade-up">
                  <td className="px-5 py-3 font-mono text-[var(--muted)] tabular-nums" data-testid={`category-number-${c.id}`}>
                    {c.number != null ? String(c.number).padStart(2, "0") : "—"}
                  </td>
                  <td className="px-5 py-3 font-medium">{c.name}</td>
                  <td className="px-5 py-3 text-[var(--muted)]">{c.description || "—"}</td>
                  <td className="px-5 py-3 text-right">
                    <div className="inline-flex gap-1">
                      <Btn variant="ghost" onClick={() => setEditing(c)} data-testid={`edit-category-${c.id}`} className="h-8 px-2"><PencilSimple size={14} /></Btn>
                      <Btn variant="ghost" onClick={() => del(c)} data-testid={`delete-category-${c.id}`} className="h-8 px-2 text-[#FF3B30] hover:bg-[#FF3B30]/5"><Trash size={14} /></Btn>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
